import { BaseHit, Hit } from 'instantsearch.js';
import { Product } from 'shared/types/product';
import { getLocalizationInfo } from 'project.config';
import { localeMapping } from 'helpers/constants/localeMapping';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';

interface ProductHitVariant {
  id: string;
  sku: string;
  images: string[];
  prices: Record<string, { centAmount: number; fractionDigits: number }>;
  isOnStock: boolean;
  productcode?: string;
  // Make sure this is required if expected
  attributes?: {
    'item-code': string;
    'colour': string;
  };
}

interface Slug {
  [key: string]: string;
}
interface ProductHit extends BaseHit {
  productcode?: string; // Fix typo here
  productId: string;
  categories?: any;
  categoryKeys?: any;
  name: any;
  attributesAlgolia?: any;
  matchedVariants?: any;
  masterVariantID: any;
  variants: Array<ProductHitVariant>;
  slug: Slug;
  selectedVariant: any;
}

/** Default tile variant when the product offers one of these colours, in order of preference. */
const PREFERRED_DEFAULT_COLOURS = ['chrome', 'stainless steel', 'stainless', 'brushed nickel'];

/** Points `masterVariantID` at the most preferred colour variant, if any (mutates the hit). */
const setMasterVariantToChromeVariant = (productHit: Hit<ProductHit>) => {
  if (!productHit) return;
  for (const colour of PREFERRED_DEFAULT_COLOURS) {
    const variant = productHit.variants?.find((v) => v.attributes?.colour?.toLowerCase() === colour);
    if (variant) {
      productHit.masterVariantID = variant.id;
      return;
    }
  }
};

/**
 * Algolia hit → the `Product` the product tile renders. Shared by every listing, on both paths.
 *
 * The tile's variant (image, code, swatch, PDP link) is picked in this order:
 *   1. the variant matching `selectedColour` — the colour facet refinement, so a colour filter shows
 *      that colour on every tile;
 *   2. the variant whose item-code matches `searchQuery` — a /search term, or the listed item-code of a
 *      "Product IDs" block;
 *   3. the master variant, re-pointed at a preferred default colour when the product has one;
 *   4. the first variant.
 */
export const mapProduct = (hit: Hit<BaseHit>, locale: string, searchQuery?: string, selectedColour?: string): Product => {
  const productHit = hit as Hit<ProductHit>;
  const localeKey = localeMapping[locale];

  const { locale: currentLocale } = getLocalizationInfo(locale);
  const slug = (productHit.slug?.[currentLocale.replace('_', '-')] ?? productHit.slug?.[`${localeKey}`])
    ?.toLowerCase()
    ?.replace(/\s/g, '-')
    ?.replace('/', '');
  setMasterVariantToChromeVariant(productHit);
  const masterVariant = productHit?.variants?.filter((variant) => variant.id === productHit?.masterVariantID)?.[0];
  const findSkuByItemCode = (itemCode: string) => {
    if(itemCode){
      const matchingVariant = findSkuByItemCodeExactMatch(itemCode) 
        || 
        productHit?.variants?.find((variant: any) =>
          variant?.attributes?.['item-code']?.toLowerCase().includes(itemCode.toLowerCase())
        );
      return matchingVariant;
    }
    else 
      return null;
  };

  const findSkuByItemCodeExactMatch = (itemCode: string) => {
    const matchingVariant = productHit?.variants?.find((variant: any) =>
      variant?.attributes?.['item-code']?.toLowerCase() == itemCode.toLowerCase()
    );
    return matchingVariant;
  };

  const colourVariant = selectedColour
    ? productHit?.variants?.find((variant: any) => {
        const colour = variant?.attributes?.colour || variant?.attributes?.color;
        const variantName = variant?.attributes?.['variant-name'];
        const target = selectedColour.trim().toLowerCase();
        if (colour && typeof colour === 'string') {
          const c = colour.trim().toLowerCase();
          if (c === target || c.startsWith(`${target} -`) || c.startsWith(`${target}-`) || c.startsWith(`${target} /`) || c.startsWith(`${target}/`)) return true;
        }
        if (variantName && typeof variantName === 'string') {
          const vn = variantName.trim().toLowerCase();
          if (vn === target || vn.startsWith(`${target} -`) || vn.startsWith(`${target}-`) || vn.startsWith(`${target} `)) return true;
        }
        return false;
      })
    : null;

  const matchedSku = colourVariant ?? findSkuByItemCode(searchQuery ?? '') ?? masterVariant ?? productHit?.variants?.[0];
  const productId = String(
    productHit?.productId || productHit?.objectID || productHit?.id || matchedSku?.attributes?.['item-code'] || matchedSku?.sku || ''
  );

  return {
    productId,
    productcode: matchedSku?.attributes?.['item-code'],
    name: productHit?.name?.[currentLocale.replace('_', '-')],
    selectedVariant: matchedSku,
    categories: productHit?.categoryKeys?.[currentLocale.replace('_', '-')]?.map((category: any) => ({
      categoryId: category,
      subCategories: [],
    })),
    variants: productHit?.variants?.map((variant) => ({ ...variant })),
    masterVariantID: productHit?.masterVariantID,
    _url: getPdpUrl(slug, matchedSku?.sku, matchedSku?.attributes?.colour),
    attributesAlgolia: productHit?.attributes,
  };
};

